import type {
  BookingListParams,
  BookingPayload,
  BookingPayloadResponse,
  BookingUpdateParams,
  CursorPaginatedResponse,
  Order,
  OrderDetail,
  OrderSearchParams,
} from '@/libs/interfaces'
import { OrderStatus } from '@/libs/interfaces'

import { mockBookings } from '../fixtures/bookings'
import { mockCustomers, mockVehicles } from '../fixtures/seed'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const bookingStore = createEntityStore<Order, 'orderID'>(mockBookings, 'orderID')

const filterBookings = (params: BookingListParams): Order[] =>
  bookingStore
    .getAll()
    .filter((order) => (params.storeID ? order.storeID === params.storeID : true))
    .filter((order) =>
      params.orderStatusSearch ? String(order.orderStatus) === params.orderStatusSearch : true,
    )
    .filter((order) => matchesSearch(order, params.search, ['orderID']))

const buildOrderTotal = (body: Partial<BookingPayload>): number[] => [
  ...(body.services ?? []).map((bookedService) => bookedService.service.total ?? 0),
  ...(body.products ?? []).map((bookedProduct) => bookedProduct.total ?? 0),
]

const buildOrderFromPayload = (body: Partial<BookingPayload>, total: number[]): Order => ({
  orderID: genID('order'),
  orderNumber: bookingStore.getAll().length + 1001,
  driver: mockCustomers.find((customer) => customer.driverID === body.driverID),
  driverCar: mockVehicles.find((vehicle) => vehicle.driverCarID === body.driverCarID),
  submissionTime: body.submissionTime ?? nowISO(),
  submittedTime: body.submittedTime,
  pickupTime: body.pickupTime ?? nowISO(),
  pickedUpTime: body.pickedUpTime,
  updatedAt: nowISO(),
  orderStatus: body.orderStatus ?? OrderStatus.preliminary,
  total,
  currency: total.map(() => body.currency ?? 'SEK'),
  billed: false,
  services: body.services ?? [],
  products: body.products ?? [],
  orgID: body.orgID,
  storeID: body.storeID,
})

const toOrderDetail = (order: Order): OrderDetail => ({
  orderID: order.orderID,
  orderNumber: order.orderNumber,
  orgID: order.orgID,
  driverCarID: order.driverCar?.driverCarID,
  driverID: order.driver?.driverID,
  storeID: order.storeID,
  submissionTime: order.submissionTime,
  submittedTime: order.submittedTime,
  pickupTime: order.pickupTime,
  pickedUpTime: order.pickedUpTime,
  orderStatus: order.orderStatus,
  currency: order.currency?.[0] ?? 'SEK',
  discount: 0,
  createdAt: order.submissionTime,
  updatedAt: order.updatedAt,
  driver: order.driver,
  bookedBy: undefined,
  cost: (order.total ?? []).reduce((sum, value) => sum + value, 0),
  services: order.services,
  products: order.products,
  packageDeals: [],
})

export const bookingsApiMock = {
  getById: async ({
    orderID,
  }: {
    orgID: string
    storeID: string
    orderID: string
  }): Promise<OrderDetail> => {
    const order = bookingStore.getById(orderID)
    return delay(order ? toOrderDetail(order) : ({} as OrderDetail))
  },
  create: async (body: Partial<BookingPayload>): Promise<BookingPayloadResponse> => {
    const total = buildOrderTotal(body)
    const order = buildOrderFromPayload(body, total)
    bookingStore.insert(order)

    return delay({
      order,
      cost: total.reduce((sum, value) => sum + value, 0),
      rentCarBooking: body.rentCarBooking ?? {},
      services: order.services ?? [],
      products: order.products ?? [],
    })
  },
  list: async (params: BookingListParams): Promise<Order[]> => delay(filterBookings(params)),
  search: async (params: OrderSearchParams): Promise<Order[]> =>
    delay(
      bookingStore
        .getAll()
        .filter((order) => order.orgID === params.orgID)
        .filter((order) => (params.driverID ? order.driver?.driverID === params.driverID : true))
        .filter((order) => (params.orderIDs ? order.orderID === params.orderIDs : true)),
    ),
  listCursor: async (params: BookingListParams): Promise<CursorPaginatedResponse<Order>> =>
    delay(paginate(filterBookings(params), params)),
  update: async (payload: BookingUpdateParams): Promise<OrderDetail> => {
    const { data } = payload
    const updated = bookingStore.update(payload.params.orderID, {
      ...(data.orderStatus && { orderStatus: data.orderStatus }),
      ...(data.submissionTime && { submissionTime: data.submissionTime }),
      ...(data.submittedTime && { submittedTime: data.submittedTime }),
      ...(data.pickupTime && { pickupTime: data.pickupTime }),
      ...(data.pickedUpTime && { pickedUpTime: data.pickedUpTime }),
      ...(data.services && { services: data.services }),
      ...(data.products && { products: data.products }),
      ...(data.driverCarID && {
        driverCar: mockVehicles.find((vehicle) => vehicle.driverCarID === data.driverCarID),
      }),
      ...(data.driverID && {
        driver: mockCustomers.find((customer) => customer.driverID === data.driverID),
      }),
      updatedAt: nowISO(),
    })
    return delay(updated ? toOrderDetail(updated) : ({} as OrderDetail))
  },
}
